import { ENTITY_COLORS } from '../../appearance/Colors';
import { t as lnT } from '../../i18n';
import { format } from 'date-fns';
import { parseEventDate } from '../../util/eventDate';
import { FiBookOpen, FiCalendar, FiCheckSquare, FiFilm, FiMapPin, FiShoppingBag } from 'react-icons/fi';
import { FaDumbbell, FaGraduationCap, FaSuitcase, FaUtensils } from 'react-icons/fa6';

export const LIST_ARTIFACT_TYPES = ['shopping_list', 'packing_list', 'checklist'];
export const LIBRARY_ARTIFACT_TYPES = ['event', 'film', 'movie', 'place', 'recipe', 'workout', 'book', 'tutorial', 'repair', 'procedure', 'product'];
export const ACTIVE_LIST_STATUSES = ['NONE', 'WANT', 'IN_PROGRESS'];
export const isListArtifact = artifact => LIST_ARTIFACT_TYPES.includes(artifact?.type);
export const isLibraryArtifact = artifact => LIBRARY_ARTIFACT_TYPES.includes(artifact?.type);
export const isCheckableSection = section => section.checkable || ['items', 'steps', 'materials', 'tools'].includes(section.kind);
export const getCheckableEntryIds = artifact => (artifact?.sections || []).flatMap(section =>
    isCheckableSection(section) ? (section.entries || []).map((entry, index) => String(entry?.id || `${section.id}-${index}`)) : []
);
export const isCompletedList = (artifact, state = artifact?.viewerState) => {
    if (!isListArtifact(artifact)) return false;
    if (state?.status === 'COMPLETED') return true;
    const ids = getCheckableEntryIds(artifact);
    const checked = new Set(state?.checkedEntryIds || []);
    return ids.length > 0 && ids.every(id => checked.has(id));
};

export const getArtifactColors = artifact => {
    const type = artifact?.semanticTypes?.includes('tv_show') ? 'tv_show' : artifact?.type;
    return ENTITY_COLORS[type] || ENTITY_COLORS.other;
};

export const getArtifactPresentation = artifact => {
    const colors = getArtifactColors(artifact);
    if (artifact?.semanticTypes?.includes('tv_show')) return { label: lnT('common:series'), Icon: FiFilm, color: colors.text };
    switch (artifact?.type) {
    case 'event': return { label: lnT('messages:event'), Icon: FiCalendar, color: colors.text };
    case 'film':
    case 'movie': return { label: lnT('common:film'), Icon: FiFilm, color: colors.text };
    case 'place': return { label: lnT('common:place'), Icon: FiMapPin, color: colors.text };
    case 'recipe': return { label: lnT('common:recipe'), Icon: FaUtensils, color: colors.text };
    case 'workout': return { label: lnT('common:workout'), Icon: FaDumbbell, color: colors.text };
    case 'book': return { label: lnT('common:book'), Icon: FiBookOpen, color: colors.text };
    case 'shopping_list': return { label: lnT('common:shoppinglist'), Icon: FiShoppingBag, color: colors.text };
    case 'packing_list': return { label: lnT('common:packinglist'), Icon: FaSuitcase, color: colors.text };
    case 'checklist': return { label: lnT('common:checklist'), Icon: FiCheckSquare, color: colors.text };
    case 'tutorial':
    case 'repair':
    case 'procedure': return { label: lnT('common:guide'), Icon: FaGraduationCap, color: colors.text };
    case 'product': return { label: lnT('common:product'), Icon: FiShoppingBag, color: colors.text };
    default: return { label: lnT('common:saveditem'), Icon: FiBookOpen, color: colors.text };
    }
};

export { parseEventDate } from '../../util/eventDate';

export const getEventTimingInfo = (artifact, translate = lnT) => {
    const type = String(artifact?.type || '').toLowerCase();
    if (type !== 'event') return null;
    const metadataDate = Array.isArray(artifact?.metadata)
        ? artifact.metadata.find(m => m?.key === 'date' || m?.key === 'startDate' || m?.key === 'eventDate')?.value
        : null;
    const rawValue = artifact?.event?.startDate || artifact?.event?.rawDate || metadataDate;
    const dateParts = parseEventDate(rawValue);
    if (!dateParts) return null;

    const month = format(dateParts.date, 'MMM').toUpperCase();
    const day = format(dateParts.date, 'd');
    const formattedDate = format(dateParts.date, 'EEE, MMM d, yyyy');

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateParts.date);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let timing = '';
    let isPast = false;
    if (diffDays < 0) {
        timing = translate('messages:passed', 'passed');
        isPast = true;
    } else if (diffDays === 0) {
        timing = translate('messages:today', 'today');
    } else if (diffDays === 1) {
        timing = translate('messages:tomorrow', 'tomorrow');
    } else {
        timing = translate('messages:inDays', { count: diffDays, defaultValue: `in ${diffDays} days` });
    }

    return {
        month,
        day,
        formattedDate,
        startTime: artifact?.event?.startTime || null,
        timing,
        isPast,
        diffDays
    };
};
